import React, { memo, useMemo, useRef } from "react";
import {
  AppBar,
  Box,
  Breadcrumbs,
  CssBaseline,
  Link,
  Toolbar,
  Typography,
} from "@mui/material";
import { AttachMoney, Business, Category, NavigateNext } from "@mui/icons-material";

import Logo from "../../assets/Logo.svg";
import {
  CategoriesContent,
  SpendContent,
  SupplierContent,
  SupplierContentCPO,
} from "./SpendOverview";
import AnalyticsSectionCM from "./Category Manager/AnalyticsSectionCM";
import AnalyticsSectionCPO from "./Category Manager/AnalyticsSectionCPO";

export const SPEND_DASHBOARD_SECTION_MAPPING = {
  "Spend Overview": "Company Details",
  Category: "Category Spend Summary",
  Carbon: "Carbon Emissions",
  Supplier: "Supplier Overview",
};

export const SpendDashboardAnalyticsSection = memo(
  function SpendDashboardAnalyticsSection({
    roleTitle,
    clientNames,
    currentSection,
    currentSubSection,
    handleBreadcrumbClick,
    spendSummary,
    categorySummary,
    supplierSummary,
    top3CategoriesSup,
    supDandP,
    supCustInfo,
    riskScore,
    scoreCommentary,
    revenue,
    supplierNews,
    supplierProData,
    kpdbData,
    topcardCommentary,
    costStructureMovements,
    topMovementsAnalysis,
    porterForces,
    supplierChartData,
    globalSummaries,
    indiSummaries,
    filteredResults,
    carbonTable,
    carbonEmission,
    marketImplications,
    catandSpend,
    csSrc,
    categoryNews,
    categoryChartData,
    categoryChartDataL1,
    topSuppliers,
    demandandSupply,
    costStructure,
    categoryDict,
    marketSize,
    topContributors,
    topCategories,
    heatmapChartData,
  }) {
    const componentRef = useRef(null);

    const cpoCardOverallSpend = useMemo(
      () => ({
        title: "Overall Spend",
        icon: <AttachMoney />,
        content: (
          <SpendContent
            spendSummary={spendSummary}
            roleTitle={roleTitle}
            clientNames={clientNames}
          />
        ),
      }),
      [clientNames, roleTitle, spendSummary]
    );

    const cpoCardTotalEmissions = useMemo(
      () => ({
        title: "Total Emissions (KgCO₂)",
        icon: <Category />,
        content: (
          <CategoriesContent
            categorySummary={categorySummary}
            roleTitle={roleTitle}
            clientNames={clientNames}
          />
        ),
      }),
      [categorySummary, clientNames, roleTitle]
    );

    const cpoCardTotalSuppliers = useMemo(
      () => ({
        title: "Total Suppliers",
        icon: <Business />,
        content: (
          <SupplierContent
            supplierSummary={supplierSummary}
            roleTitle={roleTitle}
            clientNames={clientNames}
          />
        ),
      }),
      [clientNames, roleTitle, supplierSummary]
    );

    const cpoCardTopSuppliers = useMemo(
      () => ({
        title: "Suppliers Driving 80% of Spend",
        icon: <Business />,
        content: (
          <SupplierContentCPO
            supplierSummary={supplierSummary}
            roleTitle={roleTitle}
          />
        ),
      }),
      [roleTitle, supplierSummary]
    );

    const cmCardOverallSpend = useMemo(
      () => ({
        title: "Overall Spend",
        icon: <AttachMoney />,
        content: (
          <SpendContent
            spendSummary={spendSummary}
            roleTitle={roleTitle}
            clientNames={clientNames}
          />
        ),
      }),
      [clientNames, roleTitle, spendSummary]
    );

    const cmCardCategories = useMemo(
      () => ({
        title: "Categories",
        icon: <Category />,
        content: (
          <CategoriesContent
            categorySummary={categorySummary}
            roleTitle={roleTitle}
            clientNames={clientNames}
          />
        ),
      }),
      [categorySummary, clientNames, roleTitle]
    );

    const cmCardSuppliers = useMemo(
      () => ({
        title: "Suppliers",
        icon: <Business />,
        content: (
          <SupplierContent
            supplierSummary={supplierSummary}
            roleTitle={roleTitle}
            clientNames={clientNames}
          />
        ),
      }),
      [clientNames, roleTitle, supplierSummary]
    );

    return (
      <Box
        ref={componentRef}
        sx={{
          display: "flex",
          position: "relative",
          minHeight: "100vh",
          minWidth: "100%",
        }}
      >
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: "100%",
            height: "65px",
            opacity: 1,
            borderBottom: "1px solid #2C2D3C",
            paddingLeft: "35px",
            bgcolor: "#181921",
          }}
        >
          <Toolbar
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              paddingRight: "16px",
            }}
          >
            <Typography
              variant="h4"
              noWrap
              component="div"
              style={{
                fontStyle: "Roboto",
                fontSize: "18px",
                fontWeight: 600,
                textAlign: "left",
                marginLeft: "2rem",
              }}
            >
              <img src={Logo} alt="Logo" style={{ width: "159px", height: "40px" }} />
            </Typography>

            <Breadcrumbs
              aria-label="breadcrumb"
              separator={<NavigateNext fontSize="small" />}
              sx={{
                display: "flex",
                alignItems: "center",
                color: "rgba(255, 255, 255, 0.6)",
                fontStyle: "Roboto",
                fontSize: "14px",
                textDecoration: "none",
                "& .MuiBreadcrumbs-separator": {
                  margin: "0 8px",
                },
              }}
            >
              {Object.keys(SPEND_DASHBOARD_SECTION_MAPPING).map((section) => (
                <Link
                  key={section}
                  color="inherit"
                  href="#"
                  onClick={(event) =>
                    handleBreadcrumbClick(
                      event,
                      SPEND_DASHBOARD_SECTION_MAPPING[section]
                    )
                  }
                  sx={{
                    textDecoration: "none",
                    fontWeight:
                      currentSection === SPEND_DASHBOARD_SECTION_MAPPING[section]
                        ? "bold"
                        : "normal",
                    color:
                      currentSection === SPEND_DASHBOARD_SECTION_MAPPING[section]
                        ? "white"
                        : "rgba(255, 255, 255, 0.6)",
                    "&:hover": {
                      fontWeight: "bold",
                      color: "white",
                    },
                    transition: "all 0.3s ease",
                    background:
                      currentSection === SPEND_DASHBOARD_SECTION_MAPPING[section]
                        ? "#BE3FF5"
                        : "inherit",
                    padding: "10px",
                    borderRadius: "4px",
                  }}
                >
                  {section}
                </Link>
              ))}
            </Breadcrumbs>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            width: "100%",
          }}
        >
          {roleTitle === "Category Manager" && (
            <AnalyticsSectionCM
              top3CategoriesSup={top3CategoriesSup}
              supDandP={supDandP}
              supCustInfo={supCustInfo}
              riskScore={riskScore}
              scoreCommentary={scoreCommentary}
              revenue={revenue}
              supplierNews={supplierNews}
              spendSummary={spendSummary}
              supplierProData={supplierProData}
              kpdbData={kpdbData}
              roleTitle={roleTitle}
              topcardCommentary={topcardCommentary}
              costStructureMovements={costStructureMovements}
              topMovementsAnalysis={topMovementsAnalysis}
              porterForces={porterForces}
              supplierChartData={supplierChartData}
              globalSummaries={globalSummaries}
              indiSummaries={indiSummaries}
              categorySummary={categorySummary}
              filteredResults={filteredResults}
              carbonTable={carbonTable}
              supplierSummary={supplierSummary}
              carbonEmission={carbonEmission}
              marketImplications={marketImplications}
              catandSpend={catandSpend}
              csSrc={csSrc}
              categoryNews={categoryNews}
              clientNames={clientNames}
              categoryChartData={categoryChartData}
              categoryChartDataL1={categoryChartDataL1}
              topSuppliers={topSuppliers}
              demandandSupply={demandandSupply}
              costStructure={costStructure}
              categoryDict={categoryDict}
              marketSize={marketSize}
              topContributors={topContributors}
              topCategories={topCategories}
              card={cmCardOverallSpend}
              card1={cmCardCategories}
              card2={cmCardSuppliers}
            />
          )}

          {roleTitle === "Chief Procurement Officer" && (
            <AnalyticsSectionCPO
              heatmapChartData={heatmapChartData}
              costStructureMovements={costStructureMovements}
              categoryChartDataL1={categoryChartDataL1}
              topMovementsAnalysis={topMovementsAnalysis}
              topcardCommentary={topcardCommentary}
              spendSummary={spendSummary}
              categorySummary={categorySummary}
              supplierSummary={supplierSummary}
              roleTitle={roleTitle}
              filteredResults={filteredResults}
              categoryNews={categoryNews}
              globalSummaries={globalSummaries}
              indiSummaries={indiSummaries}
              categoryChartData={categoryChartData}
              carbonEmission={carbonEmission}
              carbonTable={carbonTable}
              supplierChartData={supplierChartData}
              top3CategoriesSup={top3CategoriesSup}
              supDandP={supDandP}
              supCustInfo={supCustInfo}
              riskScore={riskScore}
              scoreCommentary={scoreCommentary}
              revenue={revenue}
              supplierNews={supplierNews}
              supplierProData={supplierProData}
              kpdbData={kpdbData}
              cardsCPO={cpoCardOverallSpend}
              cardsCPO1={cpoCardTotalEmissions}
              cardsCPO2={cpoCardTotalSuppliers}
              cardsCPO3={cpoCardTopSuppliers}
              currentSection={currentSection}
              currentSubSection={currentSubSection}
            />
          )}
        </Box>
      </Box>
    );
  }
);

